import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Send, CheckCircle, MapPin, Phone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    program: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mock form submission
    if (formData.name && formData.email && formData.message) {
      try {
        // Send contact form to backend
        const response = await axios.post(`${API}/contact`, {
          name: formData.name,
          email: formData.email,
          program: formData.program,
          message: formData.message
        });

        if (response.data) {
          setIsSubmitted(true);
          toast({
            title: "Message Sent!",
            description: "We'll get back to you within 24 hours.",
            variant: "default"
          });
          
          // Reset form after 3 seconds
          setTimeout(() => {
            setFormData({ name: '', email: '', program: '', message: '' });
            setIsSubmitted(false);
          }, 3000);
        }
      } catch (error) {
        console.error('Contact form error:', error);
        toast({
          title: "Submission Failed",
          description: "There was an error sending your message. Please try again.",
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
    }
  };

  return (
    <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Get Involved
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Ready to expand your horizons? Reach out to join Telfer Gateway Society or learn more about upcoming events.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="p-8 sm:p-10 bg-white border-2 border-slate-200 shadow-xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-slate-700 font-semibold mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  className="border-2 border-slate-200 focus:border-[#8B1538] transition-colors"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700 font-semibold mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john.doe@uottawa.ca"
                  required
                  className="border-2 border-slate-200 focus:border-[#8B1538] transition-colors"
                />
              </div>

              <div>
                <Label htmlFor="program" className="text-slate-700 font-semibold mb-2 block">
                  Program / Year
                </Label>
                <Input
                  id="program"
                  name="program"
                  type="text"
                  value={formData.program}
                  onChange={handleChange}
                  placeholder="BCom, 2nd Year"
                  className="border-2 border-slate-200 focus:border-[#8B1538] transition-colors"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-700 font-semibold mb-2 block">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your interests, questions, or how you'd like to get involved..."
                  rows={5}
                  required
                  className="border-2 border-slate-200 focus:border-[#8B1538] transition-colors resize-none"
                />
              </div>

              <Button
                type="submit"
                className={`w-full py-6 text-lg font-semibold transition-all duration-300 group ${
                  isSubmitted
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-[#8B1538] hover:bg-[#6B1028]'
                }`}
                disabled={isSubmitted}
              >
                {isSubmitted ? (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Message Sent!
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="p-8 bg-white border-2 border-slate-200 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-[#8B1538]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
                  <p className="text-slate-600 mb-2">Get in touch via email for general inquiries.</p>
                  <a href="mailto:TGS.Executive@gmail.com" className="text-[#8B1538] hover:underline font-semibold">
                    TGS.Executive@gmail.com
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white border-2 border-slate-200 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-[#8B1538]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h3>
                  <p className="text-slate-600 mb-2">Find us at the Telfer School of Management.</p>
                  <p className="text-slate-700 font-medium">
                    Desmarais Building<br />
                    55 Laurier Avenue East<br />
                    Ottawa, ON K1N 6N5
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-[#8B1538] to-[#6B1028] text-white border-0 shadow-xl">
              <h3 className="text-2xl font-bold mb-4">Join Our Community</h3>
              <p className="mb-6 leading-relaxed">
                Become part of a growing network of 200+ students breaking boundaries and expanding horizons.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="secondary"
                  className="bg-white text-[#8B1538] hover:bg-slate-100 font-semibold"
                  onClick={() => window.open('https://instagram.com/tgsociety', '_blank')}
                >
                  Instagram
                </Button>
                <Button
                  variant="secondary"
                  className="bg-white text-[#8B1538] hover:bg-slate-100 font-semibold"
                  onClick={() => window.open('https://linkedin.com/company/tgsociety', '_blank')}
                >
                  LinkedIn
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
